import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuActivity, LuRefreshCw, LuCircleCheck, LuCircleX, LuCircle, LuServer } from 'react-icons/lu';
import { api } from '../utils/api';

/**
 * HealthSummary — Resource Health Dashboard widget.
 *
 * Design follows the existing "Cloud Watcher Premium" system:
 *   - glass-card base  (bg-card + border-subtle, hover border-default)
 *   - Status colours from CSS vars: --status-healthy / --status-warning / --status-critical
 *   - Labels: uppercase + letter-spacing 0.1em + 10-12px  (per design_guidelines.json)
 *   - Data values: JetBrains Mono
 *   - Icons: Lucide React, stroke-width 1.5
 *   - Micro-animations via framer-motion
 */

const STATUS_CONFIG = {
  running: {
    label: 'Running',
    icon: LuCircleCheck,
    colorVar: 'var(--status-healthy)',
    bgVar: 'var(--status-healthy-bg)',
    tailwind: 'text-green-400',
    bgTailwind: 'bg-green-400/10',
    border: 'border-green-500/20',
    testId: 'health-stat-running',
  },
  stopped: {
    label: 'Stopped',
    icon: LuCircle,
    colorVar: 'var(--status-warning)',
    bgVar: 'var(--status-warning-bg)',
    tailwind: 'text-yellow-400',
    bgTailwind: 'bg-yellow-400/10',
    border: 'border-yellow-500/20',
    testId: 'health-stat-stopped',
  },
  error: {
    label: 'Error',
    icon: LuCircleX,
    colorVar: 'var(--status-critical)',
    bgVar: 'var(--status-critical-bg)',
    tailwind: 'text-red-400',
    bgTailwind: 'bg-red-400/10',
    border: 'border-red-500/20',
    testId: 'health-stat-error',
  },
};

/**
 * Individual stat pill used inside the card.
 */
const StatPill = ({ config, value, animate = false }) => {
  const Icon = config.icon;
  return (
    <motion.div
      data-testid={config.testId}
      initial={animate ? { opacity: 0, y: 6 } : false}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-center gap-2 px-3 py-2 rounded-md border ${config.bgTailwind} ${config.border}`}
    >
      <Icon
        className={`w-3.5 h-3.5 flex-shrink-0 ${config.tailwind}`}
        strokeWidth={1.5}
      />
      <span
        className={`text-xs uppercase tracking-widest font-medium ${config.tailwind}`}
        style={{ letterSpacing: '0.1em', fontSize: '10px' }}
      >
        {config.label}
      </span>
      <span
        className={`text-sm font-mono font-semibold ml-1 ${config.tailwind}`}
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {value}
      </span>
    </motion.div>
  );
};

/**
 * Per-provider breakdown row.
 */
const ProviderRow = ({ name, data, index }) => (
  <motion.div
    key={name}
    initial={{ opacity: 0, x: -6 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.05 }}
    className="flex items-center gap-3 py-1.5"
    data-testid={`health-provider-${name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {/* Provider name */}
    <span className="text-sm text-zinc-300 flex-1 truncate">{name}</span>

    {/* Compact status dots */}
    <div className="flex items-center gap-2">
      {data.running > 0 && (
        <span
          className="text-xs font-mono text-green-400 bg-green-400/10 px-1.5 py-0.5 rounded"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {data.running}↑
        </span>
      )}
      {data.stopped > 0 && (
        <span
          className="text-xs font-mono text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {data.stopped}—
        </span>
      )}
      {data.error > 0 && (
        <span
          className="text-xs font-mono text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {data.error}!
        </span>
      )}
      <span className="text-xs text-zinc-500 font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        {data.total}
      </span>
    </div>
  </motion.div>
);

/**
 * Main HealthSummary widget/card.
 *
 * Props:
 *   className  — extra Tailwind classes forwarded to the card root
 */
const HealthSummary = ({ className = '' }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSummary = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const res = await api.getHealthSummary();
      setSummary(res.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to load health data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // ─── Loading skeleton ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        data-testid="health-summary-loading"
        className={`glass-card rounded-xl p-6 ${className}`}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="h-4 w-36 bg-zinc-800 rounded animate-pulse" />
          <div className="h-4 w-16 bg-zinc-800 rounded animate-pulse" />
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 w-28 bg-zinc-800 rounded-md animate-pulse" />
          ))}
        </div>
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-6 bg-zinc-800/60 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // ─── Error state ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div
        data-testid="health-summary-error"
        className={`glass-card rounded-xl p-6 ${className}`}
      >
        <div className="flex items-center gap-3 text-red-400">
          <LuCircleX className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">{error}</span>
        </div>
        <button
          onClick={() => fetchSummary()}
          className="mt-3 text-xs text-zinc-500 hover:text-zinc-300 transition-colors underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const { total = 0, running = 0, stopped = 0, error: errCount = 0, by_provider = {} } = summary || {};
  const providerEntries = Object.entries(by_provider);

  // Overall health colour for the header dot
  const overallHealth =
    errCount > 0 ? 'bg-red-400 pulse-slow' :
    stopped > 0  ? 'bg-yellow-400 pulse-slow' :
    total > 0    ? 'bg-green-400 pulse-slow' :
                   'bg-zinc-600';

  return (
    <motion.div
      data-testid="health-summary-card"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-card rounded-xl p-6 ${className}`}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${overallHealth}`} />
          <LuActivity className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
          <h2 className="text-base font-semibold text-zinc-200 tracking-tight">
            Resource Health
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span
              className="text-zinc-600 uppercase"
              style={{ fontSize: '10px', letterSpacing: '0.1em' }}
            >
              {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            data-testid="health-summary-refresh"
            onClick={() => fetchSummary(true)}
            disabled={refreshing}
            className="text-zinc-600 hover:text-zinc-300 transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <LuRefreshCw
              className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ── Total headline ── */}
      <div className="mb-5" data-testid="health-stat-total">
        <span
          className="text-4xl font-semibold tracking-tight text-zinc-100"
          style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.02em' }}
        >
          {total}
        </span>
        <span
          className="ml-2 text-zinc-500 uppercase"
          style={{ fontSize: '10px', letterSpacing: '0.1em' }}
        >
          Total Resources
        </span>
      </div>

      {/* ── Status pills ── */}
      <div className="flex flex-wrap gap-2 mb-6">
        <AnimatePresence>
          <StatPill config={STATUS_CONFIG.running} value={running} animate />
          <StatPill config={STATUS_CONFIG.stopped} value={stopped} animate />
          <StatPill config={STATUS_CONFIG.error}   value={errCount} animate />
        </AnimatePresence>
      </div>

      {/* ── By-provider breakdown ── */}
      {providerEntries.length > 0 && (
        <>
          <div
            className="flex items-center gap-2 mb-2"
          >
            <LuServer className="w-3 h-3 text-zinc-600" strokeWidth={1.5} />
            <span
              className="text-zinc-500 uppercase"
              style={{ fontSize: '10px', letterSpacing: '0.1em' }}
            >
              By Provider
            </span>
          </div>
          <div className="divide-y divide-zinc-800/60">
            {providerEntries.map(([name, data], index) => (
              <ProviderRow key={name} name={name} data={data} index={index} />
            ))}
          </div>
        </>
      )}

      {/* ── Empty state ── */}
      {total === 0 && (
        <div className="text-center py-4">
          <p className="text-sm text-zinc-600">No resources tracked yet</p>
        </div>
      )}
    </motion.div>
  );
};

export default HealthSummary;
